import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevItemComment } from '../models';
import { toMap } from '../../../../language/arrays';
import { Map } from '../../../../language/models';


const KEY_ACTION = ActionType.DeleteDevItemCommentRequest;
const ACTIONS    = [
  ActionType.DeleteDevItemCommentRequest,
  ActionType.DeleteDevItemCommentResponse,
  ActionType.DeleteDevItemCommentError,
];


type Payload = string;


type Meta = {
  projectId: number;
  devItemId: number;
  commentId: number;
};


/**
 * Dispatch this to delete a dev item comment.
 *
 * @param projectId - the database Id for the project containing the commented dev item
 * @param participantId - the database Id for the participant for whose dev item we're deleting a comment
 * @param devItemId - the database Id for the commented dev item
 * @param commentId = the database Id for the deleted comment
 */
export const deleteDevItemCommentAction = (
  projectId: number,
  participantId: number,
  devItemId: number,
  commentId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.DeleteDevItemComment,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        commentId: commentId,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId: projectId,
        devItemId: devItemId,
        commentId: commentId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devItemId}-${commentId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devItemId, commentId } = action.meta;

  const metaTag = `${projectId}-${devItemId}-${commentId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  let itemComments = toMap(
    R.path<DevItemComment[]>(['devPlan', 'comments', `${projectId}`, `${devItemId}`], successState) ?? [],
    comment => `${comment.commentId}`,
  );

  itemComments = R.dissoc(`${commentId}`, itemComments) as Map<DevItemComment>;

  return R.assocPath(
    ['devPlan', 'collaborators', `${projectId}`, `${devItemId}`],
    Object.values(itemComments),
    successState,
  );
};

export const deleteDevItemCommentReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
